import type { ObjectiveLookup, StepLookup } from '@/lib/protocol-labels';
import { actionsSentence, stepTitle, triggerSentence } from '@/lib/protocol-labels';
import type { Lane } from '@/lib/protocol-lanes';
import type { ExitCondition, ProtocolStepDraft } from '@matter_extractor/shared';

/**
 * What a jump says when the pointer or the focus rests on it (refonte modulaire
 * §7.1; maquette `.jpop`).
 *
 * §7.1: « L'étiquette d'un saut est "si premier facteur + n", la condition
 * complète au survol. » The corridor carries the head of the sentence, this
 * panel carries all of it - the triggers in bold, « et » and « ou » in the grey
 * register of a keyword, and the actions the condition ends on.
 *
 * ## A white panel, like every bubble of this application now
 *
 * This was written against a rule that has since been reversed (issue 103).
 * The repository used to hold that a drawn bubble would be the one dark surface
 * of a light-only application, and therefore drew a PANEL here rather than
 * anything bubble-shaped. The design system says the opposite in as many words
 * - white is the only ground of the brand, never black, even for something
 * transient - so this panel was right for a reason that turned out to be the
 * wrong one. It keeps the register of every other panel of the editor: white,
 * `border-2 gray-200`, `shadow-md`, and the frise dims nothing behind it.
 *
 * It does NOT mount `components/ui/tooltip.tsx`, and the reason is geometry
 * rather than taste: this panel is placed from the CORRIDOR's measured
 * coordinates inside the frise, not from the rectangle of an element, so the
 * generic bubble has no anchor to hold on to. The consequence is named rather
 * than hidden: being `absolute`, it can still be clipped by the frise's own
 * horizontal scroll, which the drawn bubble never can. Issue 104 carries that.
 *
 * ## Why the sentence is rebuilt here rather than read from `conditionSentence`
 *
 * `conditionSentence` returns « quand A et B ou C » as ONE string, which is
 * exactly right for the step editor's recap line and unusable here: the whole
 * point of the panel is that the factors are bold and the connectives are not.
 * The pieces come from the same `triggerSentence`, so the two readings can never
 * word a trigger differently - the property `protocol-labels.ts` exists for.
 */

export interface JumpPopoverProps {
  /** The corridor being pointed at: the panel opens on its label's end. */
  readonly lane: Lane;
  readonly steps: readonly ProtocolStepDraft[];
  readonly stepAt: StepLookup;
  readonly objectiveAt: ObjectiveLookup;
  /** An incoming corridor runs BELOW the cards, so its panel opens downwards. */
  readonly incoming: boolean;
}

/** One piece of the « quand ... » line: a factor, or the word between two. */
type ConditionPart = {
  readonly key: string;
  readonly keyword: boolean;
  readonly text: string;
};

/**
 * The condition as pieces: « et » inside a group, « ou » between groups (§8.4).
 *
 * Assembled outside the JSX so each piece can carry a key of its own - two
 * triggers of one condition may read identically (the same objective listened
 * to twice), so the text alone is not an identity.
 */
function conditionParts(
  condition: Pick<ExitCondition, 'groups'>,
  objectiveAt: ObjectiveLookup,
): ConditionPart[] {
  const parts: ConditionPart[] = [];
  condition.groups.forEach((group, groupIndex) => {
    if (groupIndex > 0) parts.push({ key: `ou-${groupIndex}`, keyword: true, text: 'ou' });
    group.forEach((trigger, triggerIndex) => {
      if (triggerIndex > 0) {
        parts.push({ key: `et-${groupIndex}-${triggerIndex}`, keyword: true, text: 'et' });
      }
      parts.push({
        key: `f-${groupIndex}-${triggerIndex}`,
        keyword: false,
        text: triggerSentence(trigger, objectiveAt),
      });
    });
  });
  return parts;
}

export function JumpPopover({ lane, steps, stepAt, objectiveAt, incoming }: JumpPopoverProps) {
  const { jump } = lane;
  const source = jump.source;
  const from = steps.find((step) => step.id === jump.from);
  const fromPlace = stepAt(jump.from);
  if (from === undefined || fromPlace === undefined) return null;

  const condition =
    source.kind === 'fallback'
      ? undefined
      : from.conditions.find((one) => one.id === source.conditionId);
  const head =
    source.kind === 'fallback'
      ? `repli · étape ${fromPlace.index + 1}`
      : `condition de sortie ${source.conditionIndex + 1} · étape ${fromPlace.index + 1}`;
  const target = stepAt(jump.to);
  /* The fallback has no action list of its own: beyond the attempts it goes
     where §7.2 says, and that is the whole of what it does. */
  const actions =
    condition === undefined
      ? target === undefined
        ? 'aller à une étape disparue'
        : `aller à ${stepTitle(target)}`
      : actionsSentence(condition.actions, stepAt, objectiveAt);

  return (
    <div
      /*
        The panel follows the corridor being pointed at, so its position is
        MEASURED geometry - the one thing this package writes as an inline
        style. Which way it opens is not: an outgoing corridor runs above the
        cards and an incoming one below, and a panel that always opened upwards
        would cover the frise for half of them.
      */
      style={{ left: lane.label.x, top: lane.label.y }}
      className={`pointer-events-none absolute z-30 w-max max-w-80 rounded-md border-2 border-gray-200 bg-white px-3 py-2.5 text-sm shadow-md ${
        incoming ? 'translate-y-2' : '-translate-y-full'
      } ${lane.label.anchor === 'end' ? '-translate-x-full' : ''}`}
    >
      <p className="font-semibold text-gray-500 text-micro uppercase tracking-wider">{head}</p>
      <p className="text-gray-800">
        {source.kind === 'fallback' ? (
          <>
            {'quand les '}
            <b className="font-semibold">{`${from.rules.attempts} essais`}</b>
            {' de l’étape sont épuisés'}
          </>
        ) : condition === undefined ? (
          'condition disparue'
        ) : (
          <>
            {'quand '}
            {conditionParts(condition, objectiveAt).map((part) =>
              part.keyword ? (
                <span key={part.key} className="mx-1 font-semibold text-gray-500 text-xs">
                  {part.text}
                </span>
              ) : (
                <b key={part.key} className="font-semibold">
                  {part.text}
                </b>
              ),
            )}
          </>
        )}
      </p>
      <p className="mt-1 font-semibold text-bleu-nuit-deep">{`→ ${actions}`}</p>
    </div>
  );
}
